/**
 * @Module: Firebase Configuration
 * @Status: Ready
 * @Summary: Firebase app + Auth + Firestore — production-correct setup
 * @Description: Uses initializeFirestore with persistentLocalCache (the modern API,
 *   replacing the deprecated enableIndexedDbPersistence). Null-safe when unconfigured.
 * @NextSteps: Add analytics when needed
 *
 * References:
 *   - https://firebase.google.com/docs/firestore/manage-data/enable-offline
 *   - https://firebase.google.com/docs/firestore/manage-data/add-data
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, type Auth } from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentSingleTabManager,
  type Firestore,
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
};

/** Returns true when at least the API key is present. */
export const isFirebaseConfigured = (): boolean => !!firebaseConfig.apiKey;

// Only initialize Firebase when configured — prevents crash with empty env vars
let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let googleProvider: GoogleAuthProvider | null = null;
let db: Firestore | null = null;

if (isFirebaseConfigured()) {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  googleProvider = new GoogleAuthProvider();

  // Use initializeFirestore (NOT getFirestore) so we can configure persistence
  // at init time. This is the modern API — enableIndexedDbPersistence is deprecated.
  // persistentLocalCache gives offline support automatically.
  // persistentSingleTabManager is correct for single-tab apps.
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentSingleTabManager(undefined),
    }),
  });
}

export { auth, googleProvider, db };
